import styled, { css, keyframes } from "styled-components";
import { PlacelistType, PlaceType } from "resources/placelists";
import TextGothic from "components/utils/TextGothic";
import category from "images/category.svg";
import map from "images/map.svg";
import { colorCode } from "resources/colors";
import { media } from "utils/media";
import { theme } from "utils/theme";
import LazyLoad from "react-lazyload";

type Props = {
  placelist: PlacelistType;
  place: PlaceType;
  placeIndex: number;
};

const PlaceItem = (props: Props) => {
  const { placelist, place, placeIndex } = props;
  const isIe = () => {
    var ua = navigator.userAgent;
    return ua.indexOf("MSIE ") > -1 || ua.indexOf("Trident/") > -1;
  };

  return (
    <Wrapper id={`place${placeIndex + 1}`}>
      <PlaceImgWrapper>
        <Frame>
          {place.images.length === 1 ? (
            <PlaceImg
              src={`/domtour/theme/workation/music/images/${placelist.id}/places/${place.images[0]}`}
              alt="place"
            />
          ) : (
            place.images.map((image, i) =>
              i === 0 ? (
                <ImgFrame
                  key={`placeImg_${i}`}
                  index={i}
                  length={place.images.length}
                >
                  <ImgVisible>
                    <Paralax index={i} length={place.images.length}>
                      <PlaceImg
                        src={`/domtour/theme/workation/music/images/${placelist.id}/places/${image}`}
                      />
                    </Paralax>
                  </ImgVisible>
                </ImgFrame>
              ) : (
                <ImgFrame
                  key={`placeImg_${i}`}
                  index={i}
                  length={place.images.length}
                >
                  <ImgVisible>
                    <Paralax index={i} length={place.images.length}>
                      <LazyLoad offset={400}>
                        <PlaceImg
                          src={`/domtour/theme/workation/music/images/${placelist.id}/places/${image}`}
                        />
                      </LazyLoad>
                    </Paralax>
                  </ImgVisible>
                </ImgFrame>
              )
            )
          )}
        </Frame>
      </PlaceImgWrapper>
      <Content>
        <TextGothic size="20px" lh={1.5} as="h2">
          {place.name}
        </TextGothic>
        <Tags>
          <Category>
            <Icon src={category} alt="icon" />
            <TextGothic size="14px" lh={1} weight="normal">
              {place.category}
            </TextGothic>
          </Category>
          <MapLink>
            <Icon src={map} alt="map" />
            <LinkText
              href={place.map}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TextGothic size="14px" lh={1} weight="normal">
                Google Maps
              </TextGothic>
            </LinkText>
          </MapLink>
        </Tags>
        {!isIe() && (
          <Spotify>
            {place.spotify.map((s, i) => (
              <iframe
                title="spotify"
                key={`spotify_${i}`}
                src={s}
                width="100%"
                height="80"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              ></iframe>
            ))}
          </Spotify>
        )}
        <Text>{place.content}</Text>
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  padding-bottom: 80px;
`;

const PlaceImgWrapper = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;

  ${media.sp`
    height: calc(100vw * ${theme.spPlaceImgAspect});
  `}
  ${media.pc`
    height: calc(715px * ${theme.pcPlaceImgAspect});
  `}
`;

const Frame = styled.div`
  position: absolute;
`;

const transformAnimationFirst = () =>
  keyframes`
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(-100%)
    }
  `;

const transformAnimation = (index: number, length: number) =>
  index === 0
    ? keyframes`
      0% {
        transform: translateX(0%);
      }
      ${100 / length}% {
        transform: translateX(-100%);
      }
      ${100 / length + 0.000001}% {
        transform: translateX(100%);
      }
      ${100 - 100 / length}% {
        transform: translateX(100%);
      }
      100% {
        transform: translateX(0%);
      }
    `
    : keyframes`
      0% {
        transform: translateX(100%);
      }
      ${-(100 / length) + (100 / length) * index}% {
        transform: translateX(100%);
      }
      ${100 / length + (100 / length) * index}% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(-100%);
      }
  `;

const ImgFrame = styled.div<{ index: number; length: number }>`
  ${media.sp`
    width: 100vw;
    height: calc(100vw * ${theme.spPlaceImgAspect});
  `}
  ${media.pc`
    width: 715px;
    height: calc(715px * ${theme.pcPlaceImgAspect})
  `}

  position: absolute;
  padding-right: 2px;
  ${(props) =>
    props.index === 0 && props.length === 2
      ? css`
          animation: ${transformAnimationFirst()} 16s linear 0s 1 normal,
            ${transformAnimation(props.length - 1, props.length)}
              ${16 * props.length}s linear 16s infinite normal;
        `
      : props.index === 0
      ? css`
          /* animation: ${transformAnimationFirst()} 16s linear 0s 1 normal; */
          /* ,
            ${transformAnimation(props.length - 1, props.length)}
              ${16 * props.length}s linear 16s infinite normal; */
          animation: ${transformAnimation(props.index, props.length)}
            ${16 * props.length}s linear 0s infinite normal;
        `
      : css`
          animation: ${transformAnimation(props.index, props.length)}
            ${16 * props.length}s linear 0s infinite normal;
        `}
`;

const ImgVisible = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
  z-index: 0;
`;

const paralaxAnimationFirst = () =>
  keyframes`
    0%{
      transform: translateX(0%);
    }
    100%{
      transform: translateX(-5%);
    }
  `;

const paralaxAnimation = (index: number, length: number) =>
  keyframes`
    0% {
      transform: translateX(5%);
    }
    ${-(100 / length) + (100 / length) * index}% {
      transform: translateX(5%);
    }
    ${100 / length + (100 / length) * index}% {
      transform: translateX(-5%);
    }
    100% {
      transform: translateX(-5%);
    }
  `;

const Paralax = styled.div<{ index: number; length: number }>`
  position: absolute;
  width: 100%;
  left: -5%;
  ${(props) =>
    props.index === 0
      ? css`
          animation: ${paralaxAnimationFirst()} 16s linear 0s 1 normal,
            ${paralaxAnimation(props.length - 1, props.length)}
              ${16 * props.length}s linear 16s infinite normal;
        `
      : css`
          animation: ${paralaxAnimation(props.index, props.length)}
            ${16 * props.length}s linear 0s infinite normal;
        `}
`;

const PlaceImg = styled.img`
  background-color: #c4c4c4;
  object-fit: cover;
  font-family: "object-fit: cover;";
  object-position: center center;
  ${media.sp`
    width: calc(100vw * 1.1);
    height: calc(100vw * ${theme.spPlaceImgAspect});
  `}
  ${media.pc`
    width: calc(715px * 1.1);
    height: calc(715px * ${theme.pcPlaceImgAspect});
  `}
`;

const Content = styled.div`
  ${media.sp`
    padding: 16px 20px 0;
  `}
  ${media.pc`
    padding-top: 24px;
    width: 100%;
  `}
`;

const Tags = styled.div`
  display: flex;
  margin-top: 16px;
`;

const Category = styled.div`
  display: flex;
  color: ${colorCode.gray};
  align-items: center;
`;

const MapLink = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
`;

const LinkText = styled.a`
  text-decoration: underline;
  color: ${colorCode.baseGreen};
  &:hover {
    color: ${colorCode.dimGreen};
  }
`;

const Text = styled.div`
  margin-top: 16px;
  white-space: pre-wrap;
`;

const Icon = styled.img`
  position: relative;
  height: 20px;
  width: 20px;
  margin-right: 8px;
`;

const Spotify = styled.div`
  margin-top: 24px;
`;

export default PlaceItem;
